import { bind, CustomEventTarget } from '@streaming/mouflon-utils';

import { EPlayersEvents, TPlayersEventsPayload } from '@players/Players.events';
import { TMMPEventBus } from '@utils/EventBus';

import { EMouflonPlayerEvent, TMouflonPlayerEventsMap } from './Player.events';

export class PlayerEventEmitter extends CustomEventTarget<TMouflonPlayerEventsMap> {
  private readonly _onPlaybackStateChangedListener: string;
  private readonly _onVideoPlaybackStateChangedListener: string;
  private readonly _onDebuggerConnectedListener: string;
  private readonly _onErrorListener: string;
  private readonly _onResolutionsAvailableListener: string;
  private readonly _onResolutionSwitchedAutoListener: string;
  private readonly _onResolutionSwitchedListener: string;
  private readonly _onResolutionNotFoundListener: string;
  private readonly _onBroadcastSupportedChangeListener: string;
  private readonly _onStreamLoadErrorListener: string;
  private readonly _eventBusInstance: TMMPEventBus;
  constructor(eventBusInstance: TMMPEventBus) {
    super();

    this._eventBusInstance = eventBusInstance;

    this._onPlaybackStateChangedListener = this._eventBusInstance.subscribe(
      EPlayersEvents.PLAYBACK_STATE_CHANGED,
      this._onPlaybackStateChanged,
    );

    this._onResolutionSwitchedAutoListener = this._eventBusInstance.subscribe(
      EPlayersEvents.AUTO_RESOLUTION_SWITCHED,
      this._onResolutionSwitchedAuto,
    );

    this._onVideoPlaybackStateChangedListener = this._eventBusInstance.subscribe(
      EPlayersEvents.VIDEO_PLAYBACK_STATE_CHANGED,
      this._onVideoPlaybackStateChanged,
    );

    this._onDebuggerConnectedListener = this._eventBusInstance.subscribe(
      EPlayersEvents.DEBUGGER_CONNECTED,
      this._onDebuggerConnected,
    );

    this._onErrorListener = this._eventBusInstance.subscribe(EPlayersEvents.ERROR, this._onError);

    this._onResolutionsAvailableListener = this._eventBusInstance.subscribe(
      EPlayersEvents.RESOLUTIONS_AVAILABLE,
      this._onResolutionsAvailable,
    );

    this._onResolutionSwitchedListener = this._eventBusInstance.subscribe(
      EPlayersEvents.RESOLUTION_SWITCHED,
      this._onResolutionSwitched,
    );

    this._onResolutionNotFoundListener = this._eventBusInstance.subscribe(
      EPlayersEvents.RESOLUTION_NOT_FOUND,
      this._onResolutionNotFound,
    );

    this._onBroadcastSupportedChangeListener = this._eventBusInstance.subscribe(
      EPlayersEvents.BROADCAST_SUPPORTED_CHANGE,
      this._onBroadcastSupportedChange,
    );
    this._onStreamLoadErrorListener = this._eventBusInstance.subscribe(
      EPlayersEvents.STREAM_LOAD_ERROR,
      this._onStreamLoadError,
    );
  }

  public destroy(): void {
    this._eventBusInstance.unsubscribe(this._onPlaybackStateChangedListener);
    this._eventBusInstance.unsubscribe(this._onDebuggerConnectedListener);
    this._eventBusInstance.unsubscribe(this._onErrorListener);
    this._eventBusInstance.unsubscribe(this._onResolutionsAvailableListener);
    this._eventBusInstance.unsubscribe(this._onVideoPlaybackStateChangedListener);
    this._eventBusInstance.unsubscribe(this._onResolutionSwitchedAutoListener);
    this._eventBusInstance.unsubscribe(this._onResolutionSwitchedListener);
    this._eventBusInstance.unsubscribe(this._onResolutionNotFoundListener);
    this._eventBusInstance.unsubscribe(this._onBroadcastSupportedChangeListener);
    this._eventBusInstance.unsubscribe(this._onStreamLoadErrorListener);
  }

  public reemitEvent<Key extends keyof TMouflonPlayerEventsMap>(
    ev: Key,
    data: Parameters<typeof this.emitEvent>[1],
  ): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.emitEvent(ev, data);
  }

  @bind
  private _onPlaybackStateChanged({
    prevState,
    nextState,
  }: TPlayersEventsPayload<EPlayersEvents.PLAYBACK_STATE_CHANGED>): void {
    this.emitEvent(EMouflonPlayerEvent.PLAYBACK_STATE_CHANGED, { prevState, nextState });
  }

  @bind
  private _onDebuggerConnected({
    debuggerUrl,
  }: TPlayersEventsPayload<EPlayersEvents.DEBUGGER_CONNECTED>): void {
    this.emitEvent(EMouflonPlayerEvent.DEBUGGER_CONNECTED, { debuggerUrl });
  }

  @bind
  private _onError({
    error,
    playerType,
    isFatal,
    errorType,
  }: TPlayersEventsPayload<EPlayersEvents.ERROR>): void {
    this.emitEvent(EMouflonPlayerEvent.ERROR, { error, playerType, isFatal, errorType });
  }

  @bind
  private _onResolutionsAvailable({
    resolutions,
  }: TPlayersEventsPayload<EPlayersEvents.RESOLUTIONS_AVAILABLE>): void {
    this.emitEvent(EMouflonPlayerEvent.RESOLUTIONS_AVAILABLE, { resolutions });
  }

  @bind
  private _onResolutionSwitchedAuto({
    name,
  }: TPlayersEventsPayload<EPlayersEvents.AUTO_RESOLUTION_SWITCHED>): void {
    this.emitEvent(EMouflonPlayerEvent.AUTO_RESOLUTION_SWITCHED, { name });
  }

  @bind
  private _onResolutionSwitched({
    name,
  }: TPlayersEventsPayload<EPlayersEvents.RESOLUTION_SWITCHED>): void {
    this.emitEvent(EMouflonPlayerEvent.RESOLUTION_SWITCHED, { resolutionName: name });
  }

  @bind
  private _onVideoPlaybackStateChanged(
    payload: TPlayersEventsPayload<EPlayersEvents.VIDEO_PLAYBACK_STATE_CHANGED>,
  ): void {
    this.emitEvent(EMouflonPlayerEvent.VIDEO_PLAYBACK_STATE_CHANGED, payload);
  }

  @bind
  private _onResolutionNotFound(): void {
    this.emitEvent(EMouflonPlayerEvent.RESOLUTION_NOT_FOUND, {});
  }

  @bind
  private _onBroadcastSupportedChange({
    isSupported,
  }: TPlayersEventsPayload<EPlayersEvents.BROADCAST_SUPPORTED_CHANGE>): void {
    this.emitEvent(EMouflonPlayerEvent.BROADCAST_SUPPORTED_CHANGE, { isSupported });
  }

  @bind
  private _onStreamLoadError({
    reason,
    httpStatusCode,
    wsStatusCode,
    signallingMessage,
  }: TPlayersEventsPayload<EPlayersEvents.STREAM_LOAD_ERROR>): void {
    this.emitEvent(EMouflonPlayerEvent.STREAM_LOAD_ERROR, {
      reason,
      httpStatusCode,
      wsStatusCode,
      signallingMessage,
    });
  }
}
